import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { APP_STATUS } from "@/lib/constants";
import { CheckCircle2, XCircle } from "lucide-react";
import { ShowMore } from "./ShowMore";

const PAGE = 5;

/**
 * Item 4 (2026-07): "My activity" — merges "Recent hour logs", "Your
 * applications" and "Hours awaiting your approval" into ONE tabbed module
 * (Hours | Applications | To approve). Every tab paginates from 5 rows —
 * a 325-log history never renders at once.
 */
export function MyActivityTabs({ hours, apps, hostPending, hosted, withdrawApp, approveHost, rejectHost }) {
  const [hourLimit, setHourLimit] = useState(PAGE);
  const [appLimit, setAppLimit] = useState(PAGE);
  const [pendingLimit, setPendingLimit] = useState(PAGE);
  const pending = (hostPending || []).filter((p) => p.status === "pending");
  // External review fix (2026-07): "To approve" only makes sense for
  // volunteers who HOST something (community events / gatherings / help
  // requests) or already have pending logs against them — a pure volunteer
  // otherwise sees a permanently-empty, confusing tab. Default tab stays
  // "Hours" either way.
  const showApprove = (hosted || []).length > 0 || pending.length > 0;

  return (
    <div className="mt-10" data-testid="my-activity-section">
      <h2 className="font-display text-2xl text-forest tracking-tight">My activity</h2>
      <Tabs defaultValue="hours" className="mt-4">
        <TabsList className="rounded-full">
          <TabsTrigger data-testid="activity-tab-hours" value="hours" className="rounded-full">Hours</TabsTrigger>
          <TabsTrigger data-testid="activity-tab-applications" value="applications" className="rounded-full">Applications</TabsTrigger>
          {showApprove && (
            <TabsTrigger data-testid="activity-tab-approve" value="approve" className="rounded-full">
              To approve{pending.length > 0 ? ` (${pending.length})` : ""}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="hours" className="mt-4">
          <div className="grid grid-cols-1 gap-3">
            {hours.length === 0 && (
              <div className="text-neutral-500 text-sm">No logs yet. Click "Log hours" to record your first contribution.</div>
            )}
            {hours.slice(0, hourLimit).map((h) => (
              <div key={h.id} className="bg-white border border-neutral-200 rounded-xl p-5 flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-medium text-forest">{h.opportunity_title || h.org_name}</div>
                  <div className="text-sm text-neutral-600 mt-0.5">{h.org_name} · {new Date(h.date).toLocaleDateString()}</div>
                  <div className="text-sm text-neutral-700 mt-2 max-w-2xl">{h.narrative}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl text-forest">{h.hours}h</div>
                  <Badge className={
                    h.status === "approved" ? "bg-forest text-white" :
                    h.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"
                  }>{h.status}</Badge>
                </div>
              </div>
            ))}
          </div>
          <ShowMore
            shown={Math.min(hourLimit, hours.length)}
            total={hours.length}
            onMore={() => setHourLimit((l) => l + PAGE)}
            testId="hour-logs-show-more"
          />
        </TabsContent>

        <TabsContent value="applications" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {apps.length === 0 && (
              <div className="text-neutral-500 text-sm">Browse <Link to="/opportunities" className="text-forest underline">opportunities</Link> to apply.</div>
            )}
            {apps.slice(0, appLimit).map((a) => {
              const st = APP_STATUS[a.status] || { label: a.status, cls: "bg-neutral-200 text-neutral-600" };
              return (
                <div key={a.id} data-testid={`application-card-${a.id}`} className="bg-white border border-neutral-200 rounded-xl p-5 hover:-translate-y-0.5 transition-transform">
                  <Link to={`/opportunities/${a.opportunity_id}`} className="block">
                    <div className="font-medium text-forest">{a.opportunity_title}</div>
                    <div className="text-xs text-neutral-500 mt-1">{a.org_name}</div>
                  </Link>
                  <div className="flex items-center justify-between mt-3">
                    <Badge className={`rounded-full border-none ${st.cls}`}>{st.label}</Badge>
                    {["applied", "accepted"].includes(a.status) && (
                      <button data-testid={`withdraw-app-${a.id}`} onClick={() => withdrawApp(a)}
                        className="text-xs text-neutral-500 hover:text-red-600 transition-colors">
                        Withdraw
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <ShowMore
            shown={Math.min(appLimit, apps.length)}
            total={apps.length}
            onMore={() => setAppLimit((l) => l + PAGE)}
            testId="applications-show-more"
          />
        </TabsContent>

        <TabsContent value="approve" className="mt-4">
          {showApprove && (<>
          <div className="grid grid-cols-1 gap-3">
            {pending.length === 0 && (
              <div className="text-sm text-neutral-500">No hours awaiting your approval.</div>
            )}
            {pending.slice(0, pendingLimit).map((p) => (
              <div key={p.id} className="bg-white border border-neutral-200 rounded-xl p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium text-forest">{p.volunteer_name}</div>
                    <div className="text-sm text-neutral-600">{p.opportunity_title || "—"} · {p.hours}h</div>
                  </div>
                </div>
                <div className="text-sm text-neutral-700 mt-2">{p.narrative}</div>
                <div className="flex gap-2 mt-3">
                  <Button data-testid={`host-approve-${p.id}`} onClick={() => approveHost(p.id)} className="rounded-full bg-forest hover:bg-forest-hover h-8 px-4 text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                  <Button data-testid={`host-reject-${p.id}`} onClick={() => rejectHost(p.id)} variant="outline" className="rounded-full h-8 px-4 text-xs">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <ShowMore
            shown={Math.min(pendingLimit, pending.length)}
            total={pending.length}
            onMore={() => setPendingLimit((l) => l + PAGE)}
            testId="pending-approvals-show-more"
          />
          </>)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
